import { FC, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { isNumber } from 'lodash';

import { ReactComponent as ImageDefault } from 'app/assets/icons/icon-default-item.svg';

interface ImageProps {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  radius?: number;
  className?: string;
  style?: React.CSSProperties;
  loading?: 'lazy' | 'eager';
}

const pulse = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.4; }
  100% { opacity: 1; }
`;

const Wrapper = styled.figure<{
  $width: number | string;
  $height: number | string;
  $radius: number;
  $aspectRatio?: number;
}>`
  width: ${({ $width }) => (isNumber($width) ? `${$width}px` : $width)};
  ${({ $aspectRatio, $height }) =>
    $aspectRatio
      ? `aspect-ratio: ${$aspectRatio};`
      : isNumber($height)
      ? `height: ${$height}px;`
      : `height: ${$height}`};

  margin: 0 16px 0 0;
  border-radius: ${({ $radius }) => $radius}px;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
`;

const Skeleton = styled.div<{ $radius: number }>`
  position: absolute;
  inset: 0;
  background-color: #f0f0f0;
  animation: ${pulse} 1.5s ease-in-out infinite;
  border-radius: ${({ $radius }) => $radius}px;
`;

const StyledImage = styled.img<{ $loaded: boolean; $radius: number }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: ${({ $radius }) => $radius}px;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
`;

const Image: FC<ImageProps> = ({
  src = '',
  alt = '',
  width = 32,
  height = 32,
  radius = 8,
  style,
  loading = 'lazy',
  className = '',
}) => {
  const [loaded, setLoaded] = useState(false);

  const handleOnLoad = () => setLoaded(true);

  const aspectRatio =
    isNumber(width) && isNumber(height) && height !== 0 ? width / height : undefined;

  const isValidSrc = !!src;

  return (
    <Wrapper
      $width={width}
      $height={height}
      $radius={radius}
      $aspectRatio={aspectRatio}
      className={className}
      style={style}
    >
      {isValidSrc && !loaded && <Skeleton $radius={radius} />}
      {isValidSrc ? (
        <StyledImage
          src={src}
          alt={alt}
          loading={loading}
          onLoad={handleOnLoad}
          $loaded={loaded}
          $radius={radius}
        />
      ) : (
        <ImageDefault
          onLoad={handleOnLoad}
          style={{
            width: width,
            height: height,
            borderRadius: radius,
          }}
        />
      )}
    </Wrapper>
  );
};

export default Image;
